import { AGENT_INTEGRATIONS } from '@nimblybase/contracts'
import { useEffect, useRef, useState } from 'react'
import { BUILD_PROMPT } from '../lib/clients.js'
import { CONNECTION_VERIFY_PROMPT, GROK_CONNECTION_VERIFY_PROMPT, INTEGRATION_SETUP } from '../lib/integration-setup.js'
import { CopyButton } from './ui.js'

/** Package readiness is release evidence, not this user's installed/authorized state. */
export function IntegrationTargets({ apiOrigin }: { apiOrigin: string }) {
  const matches = apiOrigin.replace(/\/+$/, '') === AGENT_INTEGRATIONS.apiOrigin
  const [selected, setSelected] = useState<typeof AGENT_INTEGRATIONS.hosts[number]['id'] | null>(null)
  const host = AGENT_INTEGRATIONS.hosts.find(h => h.id === selected)
  const setupHeading = useRef<HTMLHeadingElement>(null)
  useEffect(() => { if (selected) setupHeading.current?.focus() }, [selected])
  return (
    <section aria-label="NimblyBase plugin release readiness">
      <h2>Choose your coding tool</h2>
      <p className="sub">
        Choose a tool to see its install, authorization and verification steps. These UAT packages are supplied
        for controlled tests; public installation and a complete app journey are still being verified for each tool.
        Installing a package does not authorize it or create a project.
      </p>
      {!matches && <p className="notice">These packages target UAT and cannot be used for this API environment.</p>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
        {AGENT_INTEGRATIONS.hosts.map(host => (
          <article className="card" key={host.id} aria-label={`${host.name} integration`} style={{ padding: 16 }}>
            <h3 style={{ marginTop: 0 }}>{host.name}</h3>
            <p className="sub small">
              Package: {host.packageState === 'candidate' ? 'candidate; publisher route unverified' : `UAT development ${AGENT_INTEGRATIONS.version} prepared`}
            </p>
            {host.id === 'grokbot' ? <>
              <p className="sub small">Manual MCP setup: {host.manualMcp.setupVerified ? 'verified' : 'pending'} · Fresh authorization: {host.manualMcp.freshOAuthVerified ? 'verified' : 'pending'}</p>
              <p className="sub small">Private application test: {host.manualMcp.appVerified ? 'verified' : 'pending'} · Public listing: {host.published ? 'published' : 'pending'}</p>
            </> : <>
              <p className="sub small">Install test: {host.installationVerified ? 'verified' : 'pending'} · Authorization test: {host.oauthVerified ? 'verified' : 'pending'}</p>
              <p className="sub small">Full application test: {host.appVerified ? 'verified' : 'pending'} · Public listing: {host.published ? 'published' : 'pending'}</p>
            </>}
            <p className="sub small">{host.surface}.</p>
            <button className="btn" type="button" aria-pressed={host.id === selected}
              aria-controls="integration-setup" onClick={() => setSelected(host.id)}>
              {host.id === 'grokbot' ? `Set up ${host.name} (manual MCP)` : `Set up ${host.name}`}
            </button>
            {' · '}
            <a href={host.documentation} target="_blank" rel="noreferrer">{host.name} host instructions</a>
          </article>
        ))}
      </div>
      {host && <section id="integration-setup" className="card" aria-label={`${host.name} setup`} style={{ padding: 20, marginTop: 16 }}>
        <h3 ref={setupHeading} tabIndex={-1} style={{ marginTop: 0 }}>{host.name}: your next steps</h3>
        {!matches ? <p>This setup is unavailable for the selected API environment. No UAT connection should be added here.</p>
          : host.id === 'grokbot' ? <>
            <p className="notice">Manual UAT connection tested on personal desktop {host.manualMcp.hostVersion}. This is not a published plugin or verified package import.</p>
            <ol>
              <li><strong>Add the manual connection</strong><p>{INTEGRATION_SETUP.grokbot.install}</p>
                <p>MCP address: <code>{AGENT_INTEGRATIONS.apiOrigin}/mcp</code> <CopyButton value={`${AGENT_INTEGRATIONS.apiOrigin}/mcp`} /></p>
              </li>
              <li><strong>Review browser authorization</strong>
                <p>Check the actual client, workspace, requested access and renewal duration. Select one existing project when you only need to update it.
                  Creating a new project requires workspace access. A CLI login does not authorize Grok.</p>
                <p>{INTEGRATION_SETUP.grokbot.restart} If browser authorization does not appear, stop and inspect the existing connection;
                  a connected badge does not prove fresh approval. Do not paste a management token into chat.</p>
              </li>
              <li><strong>Verify the exact connection</strong>
                <div className="code code-prose"><pre><code>{GROK_CONNECTION_VERIFY_PROMPT}</code></pre><CopyButton value={GROK_CONNECTION_VERIFY_PROMPT} /></div>
                <p>Match the returned client ID and workspace to the authorization shown below. A saved account label may differ from its actual workspace,
                  and the application name may say Cursor. Use the client ID to identify it before restricting or disconnecting.</p>
              </li>
              <li><strong>Build or update your app</strong>
                <div className="code code-prose"><pre><code>{BUILD_PROMPT}</code></pre><CopyButton value={BUILD_PROMPT} /></div>
                <p>Give the Bot access to the intended repository on its cloud computer. Review its architecture before provisioning;
                  an existing project-only connection cannot create another project. Verify backend persistence and user isolation.</p>
              </li>
            </ol>
            <p>Fresh consent and disconnect/reconnect acceptance are pending. Team, shared-bot and mobile use have not been tested.
              Connections are account-wide, so another Bot on the same account can use them.</p>
            <p><a href="/integrations/grok-bot.md" target="_blank" rel="noreferrer">Grok Bot manual UAT guide</a></p>
          </> : <>
            <p className="notice">UAT test package only. No public download or marketplace listing is available from this page.</p>
            <ol>
              <li>
                <strong>Install the supplied test package</strong>
                <p>You need the {host.name} UAT bundle, version {AGENT_INTEGRATIONS.version}, from your test operator.
                  If you do not have it, stop here; the steps below do not download or install it.</p>
                <p>{INTEGRATION_SETUP[host.id].install} {INTEGRATION_SETUP[host.id].restart}</p>
              </li>
              <li>
                <strong>Authorize from {host.name}</strong>
                <p>Use the plugin’s NimblyBase MCP connection. In the browser approval, check the application name,
                  workspace and requested access before approving. Each tool has its own connection; a CLI login does not sign it in.</p>
                <p>MCP address: <code>{AGENT_INTEGRATIONS.apiOrigin}/mcp</code></p>
                <p>If the tool does not offer browser sign-in, stop and record the client version and error.
                  The advanced token fallback below is separate and covers a workspace, not one project.</p>
              </li>
              <li>
                <strong>Verify the workspace before building</strong>
                <p>Ask the agent:</p>
                <div className="code code-prose"><pre><code>{CONNECTION_VERIFY_PROMPT}</code></pre><CopyButton value={CONNECTION_VERIFY_PROMPT} /></div>
                <p>The answer must come from its tools and match the workspace you approved. A tool list alone does not prove authorization.
                  The agent also needs access to your code and a working build/upload route before it can deploy an app.</p>
              </li>
              <li>
                <strong>Ask it to build</strong>
                <div className="code code-prose"><pre><code>{BUILD_PROMPT}</code></pre><CopyButton value={BUILD_PROMPT} /></div>
                <p>Review the proposed architecture and schema. A working application must save data in the backend and keep each user’s data private.</p>
              </li>
            </ol>
            <p><strong>Returning later?</strong> Enable the plugin in your new session and verify the same workspace and project.
              Sign in again if access has expired. You can restrict or disconnect the intended authorization below;
              removing the plugin alone does not revoke its access.</p>
          </>}
      </section>}
      <p className="sub small">
        Approved connections appear below. A client may use a different workspace from your CLI;
        verify its workspace and project before building. A cloud-only agent also needs access to your repository
        and a working build/upload route.
      </p>
    </section>
  )
}
